import type { ConsentBehavior, ConsentState } from '../types';

const keyFor = (siteKey: string) => `orbit:consent:${siteKey}`;

export function readConsent(siteKey: string): ConsentState | null {
  try {
    const raw = localStorage.getItem(keyFor(siteKey));
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

/**
 * Why a stored choice can no longer be used. Publishers bump consent_version
 * after a policy change to legally re-ask, and expiry re-asks on a schedule.
 */
export type ConsentInvalidReason = 'expired' | 'version';

export function consentInvalidReason(
  state: ConsentState | null,
  behavior: ConsentBehavior,
): ConsentInvalidReason | null {
  if (!state) return null;

  if ((state.version ?? 1) < behavior.consent_version) {
    return 'version';
  }

  const expiresAt = state.expiresAt
    ? Date.parse(state.expiresAt)
    : Date.parse(state.updatedAt) + behavior.expiry_days * 86400000;

  if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
    return 'expired';
  }

  return null;
}

/** Stored consent, or null when it expired or predates the current version. */
export function readValidConsent(
  siteKey: string,
  behavior: ConsentBehavior,
): { consent: ConsentState | null; invalid: ConsentInvalidReason | null } {
  const stored = readConsent(siteKey);
  const invalid = consentInvalidReason(stored, behavior);

  if (invalid) {
    clearConsent(siteKey);
    return { consent: null, invalid };
  }

  return { consent: stored, invalid: null };
}

export function writeConsent(siteKey: string, state: ConsentState, behavior?: ConsentBehavior): ConsentState {
  const stamped: ConsentState = {
    ...state,
    version: state.version ?? behavior?.consent_version ?? 1,
    expiresAt:
      state.expiresAt ??
      new Date(Date.now() + (behavior?.expiry_days ?? 180) * 86400000).toISOString(),
  };

  try {
    localStorage.setItem(keyFor(siteKey), JSON.stringify(stamped));
  } catch {
    // Storage blocked (private mode): consent applies for this page view only.
  }

  return stamped;
}

export function clearConsent(siteKey: string): void {
  try {
    localStorage.removeItem(keyFor(siteKey));
  } catch {
    // Nothing to do.
  }
}
